import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { paymentsApi } from '../lib/api';
import { formatPrice } from '../lib/utils';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);

  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await paymentsApi.getStatus(sid);
      const data = response.data;
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="border-slate-200 max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-orange-500 mx-auto mb-4 animate-spin" />
              <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-900 uppercase mb-2">
                Vérification du paiement
              </h1>
              <p className="text-slate-600">Veuillez patienter...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-green-600 uppercase mb-2">
                Paiement réussi!
              </h1>
              <p className="text-slate-600 mb-4">
                Votre paiement de {formatPrice(paymentData?.amount || 0)} a été confirmé.
              </p>
              <Link to="/dashboard">
                <Button className="bg-orange-500 hover:bg-orange-600" data-testid="back-to-dashboard-btn">
                  Retour au tableau de bord
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-red-600 uppercase mb-2">
                Erreur
              </h1>
              <p className="text-slate-600 mb-4">
                Une erreur est survenue lors de la vérification du paiement.
              </p>
              <Link to="/dashboard">
                <Button variant="outline" data-testid="back-to-dashboard-btn">
                  Retour au tableau de bord
                </Button>
              </Link>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-yellow-500" />
              </div>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-yellow-600 uppercase mb-2">
                Session expirée
              </h1>
              <p className="text-slate-600 mb-4">
                La session de paiement a expiré. Veuillez réessayer.
              </p>
              <Link to="/dashboard">
                <Button variant="outline" data-testid="back-to-dashboard-btn">
                  Retour au tableau de bord
                </Button>
              </Link>
            </>
          )}

          {status === 'timeout' && (
            <>
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-10 h-10 text-yellow-500" />
              </div>
              <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-yellow-600 uppercase mb-2">
                Vérification en cours
              </h1>
              <p className="text-slate-600 mb-4">
                Le statut du paiement est en cours de traitement. Vérifiez votre email pour la confirmation.
              </p>
              <Link to="/dashboard">
                <Button variant="outline" data-testid="back-to-dashboard-btn">
                  Retour au tableau de bord
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
